/**
 * Data slice — nodes[], CRUD actions, loading state, data service.
 *
 * WQ-340: every edit that looks saved is saved. Connections persist through the
 * edge endpoints as mirrored pairs; Bulk Edit persists as a diff against server
 * state; Import data is an explicit full replace with a backup first. Any failed
 * save reloads server truth and raises `error` (shown as a banner).
 */

import type { StateCreator } from 'zustand';
import type {
  AdjacencyNode,
  ConnectionEdge,
  CreateNodeInput,
  UpdateNodeInput,
} from '../types/adjacency';
import type { DataService } from './data-service';
import { enqueue } from './persist-queue';
import type { AdjacencyStoreState } from './store-types';

/** Banner text for any persistence failure (WQ-340 §2.5). */
export const SAVE_FAILED_MESSAGE = "Couldn't save that change. The map has been reloaded from the server.";

/** Save a JSON value as a file download (the Import data backup). */
export function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export interface DataSlice {
  // State
  nodes: AdjacencyNode[];
  isLoading: boolean;
  error: string | null;
  dataService: DataService;

  // Actions
  setDataService: (service: DataService) => void;
  loadNodes: () => Promise<void>;
  /** Reload from the server without the loading state (keeps the map mounted). */
  refreshNodes: () => Promise<void>;
  /** Re-fetch two nodes after a connection change (their edge rows changed). */
  refreshPair: (a: string, b: string) => Promise<void>;
  /** Log, reload server truth and show the save-failed banner. */
  handlePersistError: (e: unknown) => Promise<void>;
  clearError: () => void;
  createNode: (input: CreateNodeInput) => Promise<AdjacencyNode>;
  updateNode: (id: string, changes: UpdateNodeInput) => Promise<void>;
  deleteNode: (id: string) => Promise<void>;
  updateEdgeAttributes: (
    sourceId: string,
    targetId: string,
    attrs: { lineStyle: ConnectionEdge['lineStyle']; lineColor: string; label?: string },
  ) => Promise<void>;
  duplicateNode: (id: string) => Promise<AdjacencyNode | null>;
  bulkCreateNodes: (inputs: CreateNodeInput[]) => Promise<AdjacencyNode[]>;
  /** Bulk Edit → Apply All: persist the edited map as a diff (never a full replace). */
  replaceAllNodes: (nodes: AdjacencyNode[]) => Promise<void>;
  /** Map → Import data: full replace via /import after an automatic backup download. */
  importMapData: (file: unknown) => Promise<void>;
}

export const createDataSlice: StateCreator<
  AdjacencyStoreState,
  [],
  [],
  DataSlice
> = (set, get) => ({
  nodes: [],
  isLoading: false,
  error: null,
  dataService: null as unknown as DataService, // Set by AdjacencyMapPage via setDataService()

  setDataService: (service: DataService) => {
    set({ dataService: service });
  },

  loadNodes: async () => {
    set({ isLoading: true, error: null });
    try {
      const nodes = await get().dataService.getAll();
      set({ nodes, isLoading: false });
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : 'Failed to load nodes',
        isLoading: false,
      });
    }
  },

  refreshNodes: async () => {
    const nodes = await get().dataService.getAll();
    set({ nodes });
  },

  refreshPair: async (a, b) => {
    const { dataService } = get();
    const [nodeA, nodeB] = await Promise.all([dataService.getById(a), dataService.getById(b)]);
    if (!nodeA || !nodeB) {
      await get().refreshNodes();
      return;
    }
    set((state) => ({
      nodes: state.nodes.map((n) => (n.id === a ? nodeA : n.id === b ? nodeB : n)),
    }));
  },

  handlePersistError: async (e) => {
    console.error('Adjacency map save failed:', e);
    try {
      await get().refreshNodes();
    } catch (reloadError) {
      console.error('Adjacency map reload failed:', reloadError);
    }
    set({ error: SAVE_FAILED_MESSAGE });
  },

  clearError: () => set({ error: null }),

  createNode: (input) => enqueue(async () => {
    try {
      get().pushHistory();
      const node = await get().dataService.create(input);
      set((state) => ({ nodes: [...state.nodes, node] }));
      return node;
    } catch (e) {
      await get().handlePersistError(e);
      throw e;
    }
  }),

  updateNode: (id, changes) => enqueue(async () => {
    try {
      get().pushHistory();
      const updated = await get().dataService.update(id, changes);
      set((state) => ({
        nodes: state.nodes.map((n) => (n.id === id ? updated : n)),
      }));
    } catch (e) {
      await get().handlePersistError(e);
      throw e;
    }
  }),

  deleteNode: (id) => enqueue(async () => {
    try {
      get().pushHistory();
      await get().dataService.delete(id);
      // WQ-339: the server deletes the node's edges (delete_node, both directions);
      // mirror that locally so other nodes don't keep dangling edges/parentIds
      // (stale lines and counts) until the next reload. No extra API calls.
      set((state) => ({
        nodes: state.nodes
          .filter((n) => n.id !== id)
          .map((n) =>
            n.edges.some((e) => e.targetId === id) || n.parentIds.includes(id)
              ? { ...n, edges: n.edges.filter((e) => e.targetId !== id), parentIds: n.parentIds.filter((p) => p !== id) }
              : n,
          ),
        selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
      }));
    } catch (e) {
      await get().handlePersistError(e);
      throw e;
    }
  }),

  updateEdgeAttributes: (sourceId, targetId, attrs) => enqueue(async () => {
    get().pushHistory();
    try {
      await get().dataService.updateEdgePair(sourceId, targetId, attrs);
      await get().refreshPair(sourceId, targetId);
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),

  duplicateNode: (id) => enqueue(async () => {
    const { nodes, dataService } = get();
    const original = nodes.find((n) => n.id === id);
    if (!original) return null;
    get().pushHistory();

    const { id: _id, createdAt: _ca, modifiedAt: _ma, ...rest } = original;
    const input: CreateNodeInput = {
      ...rest,
      name: `${original.name} (Copy)`,
      edges: [],
      parentIds: [],
      mapPosition: original.mapPosition
        ? { x: original.mapPosition.x + 0.03, y: original.mapPosition.y + 0.03 }
        : null,
      spPriorityTag: null,
      spListItemId: null,
      lastSyncedAt: null,
    };

    try {
      const node = await dataService.create(input);
      set((state) => ({ nodes: [...state.nodes, node] }));
      return node;
    } catch (e) {
      await get().handlePersistError(e);
      return null;
    }
  }),

  bulkCreateNodes: (inputs) => enqueue(async () => {
    try {
      get().pushHistory();
      const created: AdjacencyNode[] = [];
      for (const input of inputs) {
        created.push(await get().dataService.create(input));
      }
      set((state) => ({ nodes: [...state.nodes, ...created] }));
      return created;
    } catch (e) {
      await get().handlePersistError(e);
      throw e;
    }
  }),

  replaceAllNodes: (newNodes) => enqueue(async () => {
    get().pushHistory();
    try {
      await get().dataService.applySnapshot(newNodes);
      await get().refreshNodes();
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),

  importMapData: (file) => enqueue(async () => {
    get().pushHistory();
    try {
      await get().dataService.importReplace(file, downloadJson);
      await get().refreshNodes();
    } catch (e) {
      await get().handlePersistError(e);
    }
  }),
});
